import React, { useState, useEffect } from "react";
import {
  StyleSheet,
  View,
  Text,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Image,
  Platform,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { usePathname } from "expo-router";
import { useMessaging } from "@/hooks/useMessaging";
import { useThemeColor } from "@/hooks/useThemeColor";
import { AppVersion, ProductVersion } from "@/versions";
import { captureCurrentScreen, attachScreenshotFromDevice } from "@/services/screen-capture";
import { BrandColors } from "@/constants/ThemeColours";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedRating, DEFAULT_RATING_LABELS } from "@/components/ThemedRating";
import { CustomAlert } from "@/components/ThemedAlert";
import { useAuth } from "@components/auth/useAuth";
import {
  collectFeedbackDiagnostics,
  type IFeedbackDiagnostics,
} from "@/services/feedback-metadata";
import { logAndFlush } from "@/services/batch-logger";
import { FeedbackIcon } from "./FeedbackIcon";
import { FeedbackDiagnosticsBox } from "./FeedbackDiagnosticsBox";

interface IFeedbackModalProps {
  visible: boolean;
  onClose: () => void;
  initialDescription?: string;
  errorContext?: { message?: string; stack?: string };
}

/**
 * Modal component allowing users to submit structured feedback with 1-5 star rating,
 * text description, screen URL, screen resolution, app version, and contextual screenshot
 * sent directly to the Admin group via internal messaging.
 */
export function FeedbackModal({
  visible,
  onClose,
  initialDescription,
  errorContext,
}: IFeedbackModalProps) {
  const pathname = usePathname();
  const { sendNewMessage } = useMessaging({ autoFetch: false });

  // Rating starts unselected (0)
  const [rating, setRating] = useState<number>(0);
  const [description, setDescription] = useState<string>("");
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { user } = useAuth();
  const tintColor = useThemeColor({}, "tint");
  const [showDiagnosticsDetails, setShowDiagnosticsDetails] = useState<boolean>(false);
  const [diagnostics, setDiagnostics] = useState<IFeedbackDiagnostics>(() =>
    collectFeedbackDiagnostics()
  );

  useEffect(() => {
    if (visible) {
      setDiagnostics(collectFeedbackDiagnostics(user));
    }
  }, [visible, user]);

  const cardBg = useThemeColor({}, "card");
  const textColor = useThemeColor({}, "text");
  const borderColor = useThemeColor({}, "border");
  const subtextColor = useThemeColor({}, "tabIconDefault");
  const inputBg = useThemeColor({}, "background");
  const inputText = useThemeColor({}, "inputText");
  const inputReadOnlyBg = useThemeColor({}, "inputReadOnly");
  const inputReadOnlyBorder = useThemeColor({}, "inputReadOnlyBorder");
  const inputReadOnlyText = useThemeColor({}, "inputReadOnlyText");

  // Determine current screen URL representation
  const currentScreenUrl =
    Platform.OS === "web" && typeof window !== "undefined"
      ? window.location.href
      : pathname || "/";

  // Capture screenshot and initialize description when modal opens
  useEffect(() => {
    let isMounted = true;

    if (visible) {
      if (initialDescription) {
        setDescription(initialDescription);
      }
      setIsCapturingScreenshot(true);

      captureCurrentScreen()
        .then((dataUrl) => {
          if (isMounted) {
            setScreenshot(dataUrl);
          }
        })
        .finally(() => {
          if (isMounted) {
            setIsCapturingScreenshot(false);
          }
        });
    } else {
      // Reset state on close
      setDescription("");
      setRating(0);
      setScreenshot(null);
    }

    return () => {
      isMounted = false;
    };
  }, [visible, initialDescription]);

  const handleAttachScreenshot = async () => {
    try {
      setIsCapturingScreenshot(true);
      const res = await attachScreenshotFromDevice();
      if (res) {
        setScreenshot(res);
      }
    } finally {
      setIsCapturingScreenshot(false);
    }
  };

  /**
   * Submits the feedback message to the Admin group.
   */
  const handleSubmit = async () => {
    if (!description.trim()) {
      CustomAlert.alert("Feedback Required", "Please provide a brief description of your feedback.");
      return;
    }

    setIsSubmitting(true);
    try {
      const starIcons = rating > 0 ? "★".repeat(rating) + "☆".repeat(5 - rating) : "Not rated";
      const ratingLabel = rating > 0 ? (DEFAULT_RATING_LABELS[rating] || `${rating}/5`) : "None";
      const currentDiag = collectFeedbackDiagnostics(user);

      const formattedBody = [
        `### User Feedback Submission`,
        errorContext ? `> ⚠️ **Error Context Attached**: \`${errorContext.message || "Unknown error"}\`` : "",
        rating > 0 ? `- **Rating**: ${starIcons} (${ratingLabel})` : `- **Rating**: Not specified`,
        `- **Screen URL**: \`${currentScreenUrl}\``,
        `- **App Version**: \`${AppVersion}\` (Product: \`${ProductVersion}\`)`,
        `- **Submitted At**: ${currentDiag.locale.localTimestamp}`,
        errorContext?.stack ? `\n---\n#### Error Stack Trace:\n\`\`\`\n${errorContext.stack.slice(0, 1500)}\n\`\`\`` : "",
        `\n---`,
        `#### Description:`,
        description.trim(),
        `\n---`,
        screenshot && screenshot.length < 250000
          ? `\n---\n#### Screenshot:\n![Screen Context](${screenshot})`
          : "",
      ]
        .filter(Boolean)
        .join("\n");

      await sendNewMessage({
        targetType: "group",
        targetId: "system-admins",
        targetName: "Admins",
        type: "Feedback",
        subject: errorContext
          ? `[Error Feedback] on ${pathname || "Home"} (v${AppVersion})`
          : rating > 0
            ? `[Feedback] ${rating}★ on ${pathname || "Home"} (v${AppVersion})`
            : `[Feedback] on ${pathname || "Home"} (v${AppVersion})`,
        body: formattedBody,
      });

      logAndFlush("info", `[Feedback] Submitted ${rating > 0 ? `${rating}★` : "unrated"} feedback on ${pathname || "Home"} (Platform: ${Platform.OS})`);

      // Close the modal screen immediately when submitted
      onClose();

      CustomAlert.alert(
        "Thank You!",
        "Your feedback has been submitted to the Memories administration team."
      );
    } catch (err: any) {
      console.error("[FeedbackModal] Submission failed:", err);
      CustomAlert.alert(
        "Submission Failed",
        err.message || "Could not send feedback. Please try again later."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View
          style={[
            styles.modalContainer,
            { backgroundColor: cardBg, borderColor: borderColor },
          ]}
          // Attribute used by html2canvas to ignore the modal if needed
          {...{ "data-feedback-modal": "true" }}
        >
          {/* Header */}
          <View style={[styles.headerRow, { borderBottomColor: borderColor }]}>
            <View style={styles.headerTitleRow}>
              <FeedbackIcon
                size={22}
                color={BrandColors.sungoldOrange.hex}
                style={styles.headerIcon}
              />
              <Text style={[styles.headerTitle, { color: textColor }]}>
                Send Feedback
              </Text>
            </View>
            <TouchableOpacity
              onPress={onClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              accessibilityRole="button"
              accessibilityLabel="Close feedback form"
            >
              <Ionicons name="close" size={24} color={subtextColor} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.scrollArea} contentContainerStyle={styles.scrollContent}>
            <FeedbackDiagnosticsBox
              currentScreenUrl={currentScreenUrl}
              diagnostics={diagnostics}
              showDiagnosticsDetails={showDiagnosticsDetails}
              onToggleDetails={() => setShowDiagnosticsDetails((prev) => !prev)}
              inputReadOnlyBg={inputReadOnlyBg}
              inputReadOnlyBorder={inputReadOnlyBorder}
              inputReadOnlyText={inputReadOnlyText}
              tintColor={tintColor}
            />

            {/* Star Rating Section */}
            <View style={styles.sectionContainer}>
              <View style={styles.ratingHeaderRow}>
                <Text style={[styles.sectionLabel, { color: textColor, marginBottom: 0 }]}>
                  Rating <Text style={[styles.optionalMark, { color: subtextColor }]}>(optional)</Text>:
                </Text>
                {rating > 0 && (
                  <TouchableOpacity
                    onPress={() => setRating(0)}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    accessibilityRole="button"
                    accessibilityLabel="Clear rating"
                  >
                    <Text style={[styles.clearRatingText, { color: tintColor }]}>
                      Clear
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
              <ThemedRating
                value={rating}
                onChange={setRating}
                size={20}
                unselectedLabel="Select a rating (optional)"
              />
            </View>

            {/* Description Text Input */}
            <View style={styles.sectionContainer}>
              <Text style={[styles.sectionLabel, { color: textColor }]}>
                Your Feedback: <Text style={styles.requiredMark}>*</Text>
              </Text>
              <TextInput
                style={[
                  styles.textInput,
                  {
                    backgroundColor: inputBg,
                    color: inputText,
                    borderColor: borderColor,
                  },
                ]}
                placeholder="What worked well? What could be improved or fixed?"
                placeholderTextColor={subtextColor}
                multiline
                numberOfLines={4}
                value={description}
                onChangeText={setDescription}
                textAlignVertical="top"
              />
            </View>

            {/* Context Screenshot Preview */}
            <View style={styles.sectionContainer}>
              <Text style={[styles.sectionLabel, { color: textColor }]}>
                Screen Context Screenshot:
              </Text>
              {isCapturingScreenshot ? (
                <View style={[styles.screenshotPlaceholder, { borderColor: borderColor, backgroundColor: inputBg }]}>
                  <ActivityIndicator size="small" color={BrandColors.sungoldOrange.hex} />
                  <Text style={[styles.screenshotLoadingText, { color: subtextColor }]}>
                    Capturing screen context...
                  </Text>
                </View>
              ) : screenshot ? (
                <View style={[styles.screenshotContainer, { borderColor: borderColor }]}>
                  <Image
                    source={{ uri: screenshot }}
                    style={styles.screenshotImage}
                    resizeMode="contain"
                  />
                  <View style={styles.screenshotBadge}>
                    <Ionicons name="camera" size={12} color="#FFFFFF" style={{ marginRight: 4 }} />
                    <Text style={styles.screenshotBadgeText}>Captured Context</Text>
                  </View>
                </View>
              ) : (
                <View style={[styles.screenshotPlaceholder, { borderColor: borderColor, backgroundColor: inputBg }]}>
                  <Ionicons name="image-outline" size={24} color={subtextColor} />
                  <Text style={[styles.screenshotPlaceholderText, { color: subtextColor }]}>
                    Screenshot context attached via system metadata.
                  </Text>
                  <TouchableOpacity
                    style={[styles.attachScreenshotBtn, { borderColor }]}
                    onPress={handleAttachScreenshot}
                    accessibilityRole="button"
                    accessibilityLabel="Attach Screenshot or Photo from device"
                    testID="feedback-attach-screenshot-btn"
                  >
                    <Ionicons name="camera-outline" size={14} color={tintColor} style={{ marginRight: 6 }} />
                    <Text style={[styles.attachScreenshotBtnText, { color: tintColor }]}>
                      Attach Screenshot / Photo
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </ScrollView>

          {/* Actions Footer */}
          <View style={[styles.footerRow, { borderTopColor: borderColor }]}>
            <ThemedButton
              title="Cancel"
              variant="minimal"
              onPress={onClose}
              disabled={isSubmitting}
            />

            <ThemedButton
              title="Submit"
              variant="primary"
              onPress={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
              accessibilityLabel="Submit Feedback"
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  modalContainer: {
    width: "100%",
    maxWidth: 540,
    maxHeight: "90%",
    borderRadius: 0,
    borderWidth: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
    display: "flex",
    flexDirection: "column",
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderBottomWidth: 1,
  },
  headerTitleRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  headerIcon: {
    marginRight: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: "700",
    fontFamily: "serif",
  },
  scrollArea: {
    flexShrink: 1,
  },
  scrollContent: {
    padding: 20,
  },
  sectionContainer: {
    marginBottom: 18,
  },
  ratingHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  optionalMark: {
    fontSize: 12,
    fontWeight: "400",
  },
  clearRatingText: {
    fontSize: 12,
    fontWeight: "600",
    textDecorationLine: "underline",
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 8,
  },
  requiredMark: {
    color: "#DC2626",
  },
  textInput: {
    borderWidth: 1,
    borderRadius: 0,
    padding: 12,
    fontSize: 14,
    minHeight: 90,
  },
  screenshotContainer: {
    borderWidth: 1,
    borderRadius: 0,
    overflow: "hidden",
    height: 140,
    position: "relative",
    backgroundColor: "#1A1A1A",
  },
  screenshotImage: {
    width: "100%",
    height: "100%",
  },
  screenshotBadge: {
    position: "absolute",
    bottom: 6,
    right: 6,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    paddingHorizontal: 8,
    paddingVertical: 3,
    flexDirection: "row",
    alignItems: "center",
  },
  screenshotBadgeText: {
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: "600",
  },
  screenshotPlaceholder: {
    borderWidth: 1,
    borderStyle: "dashed",
    borderRadius: 0,
    height: 80,
    justifyContent: "center",
    alignItems: "center",
    padding: 8,
  },
  screenshotLoadingText: {
    marginTop: 6,
    fontSize: 12,
  },
  screenshotPlaceholderText: {
    marginTop: 4,
    fontSize: 12,
    textAlign: "center",
  },
  attachScreenshotBtn: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    borderWidth: 1,
  },
  attachScreenshotBtnText: {
    fontSize: 12,
    fontWeight: "600",
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: 1,
  },
});
